import React from 'react';

import ClaimLink from '~/components/ClaimLink';
import CliveConditionalDisplay from '~/components/Clive/CliveConditionalDisplay';
import { SortableTable } from '~/components/core';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import EmptyState from '~/components/core/EmptyState';
import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import { useCms } from '~/components/hooks/useCms';
import { AttachmentIcon } from '~/components/icons';
import { useCliveConfigOptions } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import LobCell from '~/components/TPA/LOB/LobCell';
import { isoUtcDateTimeToLocal } from '~/DateTimeUtils';
import { type FnolInboxRecord } from '~/server_shared/openapi/data-contracts';

import { useShouldDisplayCliveForInboxRecord } from '../hooks/useShouldDisplayCliveForInboxRecord';
import { FnolAiInboxStatusChip } from '../StatusChip/FnolAiInboxStatusChip';
import { FnolInboxStatusChip } from '../StatusChip/FnolInboxStatusChip';
import type { InboxType } from '../types';

interface FnolAiInboxTableProps {
  rows: FnolInboxRecord[];
  onRowClick: (row: FnolInboxRecord) => void;
  withClaimColumn?: boolean;
  selectedItemId?: string | number;
  paginationProps?: PaginationProps;
  onSortByColumn?: (columnId: string, order: 'asc' | 'desc') => void;
  defaultSortColumn?: string;
  defaultSortOrder?: 'asc' | 'desc';
  pagination?: {
    page: number;
    rowsPerPage: number;
    totalCount: number;
    resultsTruncated: boolean;
    setPage: (page: number) => void;
    setRowsPerPage: (rows: number) => void;
  };
  onRefresh?: () => void;
  isLoading?: boolean;
  inboxType: InboxType;
}

const FnolAiInboxTable: React.FC<FnolAiInboxTableProps> = ({
  rows,
  onRowClick,
  withClaimColumn,
  selectedItemId,
  paginationProps,
  onSortByColumn,
  defaultSortColumn = 'date_received',
  defaultSortOrder = 'desc',
  pagination,
  onRefresh,
  isLoading,
  inboxType,
}) => {
  const { userOrganization } = useCms();
  const { isCliveEnabledForOrganization } = useCliveConfigOptions();
  const isCliveGeneralEnabled = isCliveEnabledForOrganization();
  const shouldDisplayCliveForRow = useShouldDisplayCliveForInboxRecord();

  const onRowClickHandler = (id: number) => {
    const row = rows.find((r) => r.id === id);
    if (row) onRowClick(row);
  };

  // Determine if status column should be visible
  const isStatusColumnVisible = React.useMemo(() => {
    if (isCliveGeneralEnabled) {
      // Clive Enabled: both tabs show status column
      return true;
    } else {
      // Clive Disabled: only "done" tab shows status column, "in-queue" tab hides it
      return inboxType === 'done';
    }
  }, [isCliveGeneralEnabled, inboxType]);

  // Determine status column label
  const statusColumnLabel = React.useMemo(() => {
    if (isCliveGeneralEnabled) {
      // Clive Enabled: "in-queue" shows "Clive's Analysis", "done" shows "Status"
      return inboxType === 'done' ? 'Status' : "Clive's Analysis";
    } else {
      // Clive Disabled: when visible (done tab), always shows "Status"
      return 'Status';
    }
  }, [isCliveGeneralEnabled, inboxType]);

  const columns = [
    {
      id: 'sub_organization_name',
      label: 'Sub-Organization',
      isHidden: !userOrganization.sub_organizations_enabled,
      specialCell: (row: FnolInboxRecord) => row.sub_organization_name,
      disableSort: true,
    },
    {
      id: 'line_of_business',
      label: 'Line of Business',
      specialCell: (row: FnolInboxRecord) => row.line_of_business && <LobCell lob={row.line_of_business} />,
      disableSort: true,
    },
    {
      id: 'contact_full_name_or_email',
      label: 'Contact',
      specialCell: (row: FnolInboxRecord) => row.contact_full_name || row.email_address,
      disableSort: true,
    },
    { id: 'subject', label: 'Subject', disableSort: true, maxWidthPercentage: 25, overflow: true },
    {
      id: 'date_received',
      label: 'Time',
      specialCell: (row: FnolInboxRecord) => row.date_received && isoUtcDateTimeToLocal(row.date_received),
    },
    {
      id: 'attachments',
      label: 'Attachment',
      specialCell: (row: FnolInboxRecord) => (row.attachments ? <AttachmentIcon /> : null),
      disableSort: true,
    },
    {
      id: 'claim_number',
      label: 'Claim Number',
      isHidden: !withClaimColumn,
      specialCell: (row: FnolInboxRecord) =>
        row.claim_id && row.claim_number ? <ClaimLink claimId={row.claim_id} linkText={row.claim_number} /> : null,
      disableSort: true,
    },
    {
      id: 'status',
      label: statusColumnLabel,
      isHidden: !isStatusColumnVisible,
      specialCell: (row: FnolInboxRecord) =>
        inboxType === 'done' ? (
          <FnolInboxStatusChip status={row.status} />
        ) : (
          <CliveConditionalDisplay
            subOrganizationId={row.effective_sub_org_id}
            displayClive={shouldDisplayCliveForRow(row)}
          >
            <FnolAiInboxStatusChip status={row.status} />
          </CliveConditionalDisplay>
        ),
      disableSort: true,
    },
  ];

  // Adapter function to convert SortableTable callback signature to our interface
  const handleSortByColumn = React.useCallback(
    (_event: React.MouseEvent, { id, order }: { id: string; order: 'asc' | 'desc' }) => {
      if (onSortByColumn) {
        onSortByColumn(id, order);
      }
    },
    [onSortByColumn]
  );

  const counterViewText = (
    <CounterWithRefresh
      onRefresh={onRefresh}
      isLoading={isLoading}
      pagination={pagination}
      refreshTooltipText="Refresh inbox"
      refreshAriaLabel="Refresh inbox"
    />
  );

  // Get IDs of unread items to show in bold
  const boldRowsIds = React.useMemo(() => {
    return rows.filter((row) => row.is_read !== true).map((row) => row.id);
  }, [rows]);

  return (
    <>
      <SortableTable
        columns={columns}
        rows={rows}
        stickyHeader
        onRowClick={onRowClickHandler}
        selectedRowsIds={[selectedItemId]}
        boldRowsIds={boldRowsIds}
        emptyStateComponent={<EmptyState subtitle="No records found" />}
        defaultOrderColumn={columns.findIndex((column) => column.id === defaultSortColumn)}
        order={defaultSortOrder}
        paginationProps={paginationProps}
        onSortByColumn={handleSortByColumn}
        counterViewText={counterViewText}
      />
    </>
  );
};

export default FnolAiInboxTable;
